import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

/**
 * Phase 4 · WS5 — Hold-release rule CRUD and auto-release runner.
 *
 * Sprint 29 M0 — adds explicit policy scope (tenant | customer). Customer
 * rules override tenant rules for the same hold_reason. The manual editor
 * and the intent flow both go through these server fns; there is no
 * UI-side policy logic, no duplicate write path.
 */

export const HOLD_REASONS = ["fraud", "payment", "inventory", "credit", "manual", "other"] as const;
export const TRIGGERS = ["time_elapsed", "credit_recovered", "fraud_score_below", "manual_only"] as const;
export type HoldReason = (typeof HOLD_REASONS)[number];
export type RuleTrigger = (typeof TRIGGERS)[number];

/* ---------------------------------------------------------------------------
 * Canonical per-trigger params validator (Sprint 29 M1).
 *
 * Single source of truth for what `params` must look like for each trigger.
 * Mirrors the values the auto_release_due_holds() PG runner actually reads:
 *   - time_elapsed       -> { after_minutes: int >= 1 }
 *   - credit_recovered   -> {} (reads credit_limit/credit_balance from customers)
 *   - fraud_score_below  -> { threshold: number 0..1 }
 *   - manual_only        -> {} (never auto-releases)
 *
 * `.strict()` rejects unrelated keys so AI-proposed or hand-typed noise
 * cannot persist. Both the intent activation path and the manual editor
 * call `validateRuleParams` so behavior is identical.
 * ------------------------------------------------------------------------- */
export const RuleTriggerParamsZ = z.discriminatedUnion("trigger", [
  z.object({
    trigger: z.literal("time_elapsed"),
    params: z.object({ after_minutes: z.number().int().min(1).max(60 * 24 * 30) }).strict(),
  }),
  z.object({
    trigger: z.literal("credit_recovered"),
    params: z.object({}).strict(),
  }),
  z.object({
    trigger: z.literal("fraud_score_below"),
    params: z.object({ threshold: z.number().min(0).max(1) }).strict(),
  }),
  z.object({
    trigger: z.literal("manual_only"),
    params: z.object({}).strict(),
  }),
]);

/** Throws a readable Error if (trigger, params) doesn't match the canonical shape. */
export function validateRuleParams(trigger: RuleTrigger, params: Record<string, unknown>): void {
  const r = RuleTriggerParamsZ.safeParse({ trigger, params });
  if (!r.success) {
    const first = r.error.issues[0];
    throw new Error(
      `E_RULE_PARAMS: trigger '${trigger}' — ${first?.path.join(".") || "params"}: ${first?.message ?? "invalid"}`,
    );
  }
}

/* Shared scope shape — discriminated by `scope_level`. Server-side scope
 * validator. Mirrors the partial-unique indexes added in the M0 migration. */
export const RuleScopeZ = z.discriminatedUnion("scope_level", [
  z.object({ scope_level: z.literal("tenant"), customer_id: z.null() }),
  z.object({ scope_level: z.literal("customer"), customer_id: z.string().uuid() }),
]);
export type RuleScope = z.infer<typeof RuleScopeZ>;

const TenantZ = z.object({ tenantId: z.string().uuid() });

export const listHoldRules = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => TenantZ.parse(input))
  .handler(async ({ data, context }) => {
    const { data: rows, error } = await context.supabase
      .from("hold_release_rules")
      .select(
        "id, hold_reason, trigger, params, is_active, updated_at, source, scope_level, customer_id, customers(name)",
      )
      .eq("tenant_id", data.tenantId)
      .order("scope_level", { ascending: true })
      .order("hold_reason", { ascending: true });
    if (error) throw new Error(`E_QUERY: ${error.message}`);
    return { rows: rows ?? [] };
  });

/** Shape of a row returned by listHoldRules — usable from the UI. */
export type HoldRuleListRow = {
  id: string;
  hold_reason: HoldReason;
  trigger: RuleTrigger;
  params: Record<string, string | number | boolean | null>;
  is_active: boolean;
  updated_at: string;
  source: "manual" | "ai_proposal" | "ai_proposal_modified";
  scope_level: "tenant" | "customer";
  customer_id: string | null;
  customers: { name: string } | null;
};

/* listTenantCustomers — narrow read for the customer-scope picker. */
export const listTenantCustomers = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => TenantZ.parse(input))
  .handler(async ({ data, context }) => {
    const { data: rows, error } = await context.supabase
      .from("customers")
      .select("id, name")
      .eq("tenant_id", data.tenantId)
      .order("name", { ascending: true });
    if (error) throw new Error(`E_QUERY: ${error.message}`);
    return { rows: (rows ?? []) as Array<{ id: string; name: string }> };
  });

const UpsertZ = z.object({
  tenantId: z.string().uuid(),
  holdReason: z.enum(HOLD_REASONS),
  trigger: z.enum(TRIGGERS),
  params: z.record(z.string(), z.unknown()).default({}),
  isActive: z.boolean().default(true),
  scope: RuleScopeZ,
});

export const upsertHoldRule = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => UpsertZ.parse(input))
  .handler(async ({ data, context }) => {
    // M1: enforce per-trigger params shape here too, so the manual editor
    // cannot bypass the validation the intent activation path uses.
    validateRuleParams(data.trigger, data.params);
    // Source-provenance preservation: if an existing rule with the same
    // (tenant, scope, reason) was created from an AI proposal, flip its
    // source to `ai_proposal_modified` so audit reflects operator override.
    // Manual rules stay 'manual'; existing 'ai_proposal_modified' stays.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const sb = context.supabase as any;
    let existingSource: string | null = null;
    if (data.scope.scope_level === "tenant") {
      const { data: ex } = await sb
        .from("hold_release_rules")
        .select("source")
        .eq("tenant_id", data.tenantId)
        .is("customer_id", null)
        .eq("hold_reason", data.holdReason)
        .maybeSingle();
      existingSource = ex?.source ?? null;
    } else {
      const { data: ex } = await sb
        .from("hold_release_rules")
        .select("source")
        .eq("tenant_id", data.tenantId)
        .eq("customer_id", data.scope.customer_id)
        .eq("hold_reason", data.holdReason)
        .maybeSingle();
      existingSource = ex?.source ?? null;
    }
    const nextSource =
      existingSource === "ai_proposal" || existingSource === "ai_proposal_modified"
        ? "ai_proposal_modified"
        : "manual";

    const onConflict =
      data.scope.scope_level === "tenant"
        ? "tenant_id,hold_reason"
        : "tenant_id,customer_id,hold_reason";

    const { error } = await context.supabase
      .from("hold_release_rules")
      .upsert(
        {
          tenant_id: data.tenantId,
          customer_id: data.scope.customer_id,
          scope_level: data.scope.scope_level,
          hold_reason: data.holdReason,
          trigger: data.trigger,
          params: data.params,
          is_active: data.isActive,
          source: nextSource,
        } as never,
        { onConflict },
      );
    if (error) throw new Error(`E_WRITE: ${error.message}`);
    return { ok: true };
  });

const DeleteZ = z.object({ id: z.string().uuid() });
export const deleteHoldRule = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => DeleteZ.parse(input))
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase.from("hold_release_rules").delete().eq("id", data.id);
    if (error) throw new Error(`E_WRITE: ${error.message}`);
    return { ok: true };
  });

/* ---------------------------------------------------------------------------
 * Preview Impact
 *
 * Returns, for a given (tenant, hold_reason, trigger, params) draft:
 *   - matchingNow:    open `order_holds` rows of this reason this rule covers
 *   - matchingHistorical: holds of this reason placed in the last 90 days
 *     that this rule's release condition would have applied to
 *   - examples: up to 5 representative open holds with the reason it matched
 *
 * NOTE: this never re-implements the auto-release decision. It returns the
 * universe of holds that *fall under this rule's scope* (tenant + reason).
 * The actual release decision remains the server-side runner's job.
 * ------------------------------------------------------------------------- */

const PreviewZ = z.object({
  tenantId: z.string().uuid(),
  holdReason: z.enum(HOLD_REASONS),
  trigger: z.enum(TRIGGERS),
  params: z.record(z.string(), z.unknown()).default({}),
  scope: RuleScopeZ,
});

export const previewHoldRule = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => PreviewZ.parse(input))
  .handler(async ({ data, context }) => {
    return runHoldRulePreview(context.supabase, data);
  });

/**
 * Shared preview implementation. Used by `previewHoldRule` (manual editor)
 * and by `composePreview` (intent flow). Single source of truth for what
 * "this scope+reason matches" means — preview and activation always agree.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function runHoldRulePreview(supabase: any, data: {
  tenantId: string;
  holdReason: HoldReason;
  trigger?: RuleTrigger;
  params?: Record<string, unknown>;
  scope: RuleScope;
}) {
    const NOW = new Date();
    const ninetyDaysAgo = new Date(NOW.getTime() - 90 * 24 * 60 * 60 * 1000).toISOString();

    // Scope-aware base query. Customer scope joins `orders` to filter by
    // customer_id, matching the evaluator's customer-wins selection set.
    const baseSelect =
      data.scope.scope_level === "customer"
        ? "id, order_id, placed_at, notes, orders!inner(order_number, customer_id)"
        : "id, order_id, placed_at, notes, orders!inner(order_number)";

    function applyScope<Q extends { eq: (k: string, v: unknown) => Q }>(q: Q): Q {
      if (data.scope.scope_level === "customer") {
        return q.eq("orders.customer_id", data.scope.customer_id);
      }
      return q;
    }

    // matches right now: open holds of this reason inside scope.
    let nowQ = supabase
      .from("order_holds")
      .select(baseSelect, { count: "exact", head: true })
      .eq("tenant_id", data.tenantId)
      .eq("reason", data.holdReason)
      .is("released_at", null);
    nowQ = applyScope(nowQ);
    const { count: matchingNow, error: nowErr } = await nowQ;
    if (nowErr) throw new Error(`E_PREVIEW: ${nowErr.message}`);

    // historical: holds of this reason placed in the last 90 days, in scope.
    let histQ = supabase
      .from("order_holds")
      .select(baseSelect, { count: "exact", head: true })
      .eq("tenant_id", data.tenantId)
      .eq("reason", data.holdReason)
      .gte("placed_at", ninetyDaysAgo);
    histQ = applyScope(histQ);
    const { count: matchingHistorical, error: histErr } = await histQ;
    if (histErr) throw new Error(`E_PREVIEW: ${histErr.message}`);

    // examples: up to 5 open holds in scope.
    let exQ = supabase
      .from("order_holds")
      .select(baseSelect)
      .eq("tenant_id", data.tenantId)
      .eq("reason", data.holdReason)
      .is("released_at", null)
      .order("placed_at", { ascending: false })
      .limit(5);
    exQ = applyScope(exQ);
    const { data: exRows, error: exErr } = await exQ;
    if (exErr) throw new Error(`E_PREVIEW: ${exErr.message}`);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const examples = (exRows ?? []).map((row: any) => {
      const orderNumber = row.orders?.order_number ?? row.order_id?.slice(0, 8);
      const placed = row.placed_at as string;
      const minsAgo = Math.round((NOW.getTime() - new Date(placed).getTime()) / 60_000);
      return {
        id: row.id as string,
        label: `Order ${orderNumber}`,
        reason: `On ${data.holdReason} hold${row.notes ? ` — ${row.notes}` : ""}`,
        href: `/app/orders/${row.order_id}`,
        meta: relativeMins(minsAgo),
      };
    });

    return {
      matchingNow: matchingNow ?? 0,
      matchingHistorical: matchingHistorical ?? 0,
      examples,
    };
}

const RecentZ = z.object({
  tenantId: z.string().uuid(),
  holdReason: z.enum(HOLD_REASONS),
  limit: z.number().int().min(1).max(50).default(10),
});

/**
 * Recent decisions for this hold reason. Reads from `order_holds` directly
 * (place + release events) since hold-specific signals are not yet emitted
 * to `operational_signals`. This is purely a read of existing data.
 */
export const listRecentHoldDecisions = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => RecentZ.parse(input))
  .handler(async ({ data, context }) => {
    const { data: rows, error } = await context.supabase
      .from("order_holds")
      .select(
        "id, order_id, placed_at, released_at, released_reason, notes, orders!inner(order_number)",
      )
      .eq("tenant_id", data.tenantId)
      .eq("reason", data.holdReason)
      .order("placed_at", { ascending: false })
      .limit(data.limit);
    if (error) throw new Error(`E_QUERY: ${error.message}`);

    // Flatten place + release into a single chronological feed.
    type Decision = {
      id: string;
      at: string;
      summary: string;
      detail?: string;
      href: string;
      tone: "ok" | "warn" | "info";
    };
    const out: Decision[] = [];
    for (const r of (rows ?? []) as any[]) {
      const orderNumber = r.orders?.order_number ?? r.order_id?.slice(0, 8);
      out.push({
        id: `${r.id}:placed`,
        at: r.placed_at,
        summary: `Hold placed on Order ${orderNumber}`,
        detail: r.notes ?? undefined,
        href: `/app/orders/${r.order_id}`,
        tone: "warn",
      });
      if (r.released_at) {
        out.push({
          id: `${r.id}:released`,
          at: r.released_at,
          summary: `Hold released on Order ${orderNumber}`,
          detail: r.released_reason ?? undefined,
          href: `/app/orders/${r.order_id}`,
          tone: "ok",
        });
      }
    }
    out.sort((a, b) => (a.at < b.at ? 1 : -1));
    return { rows: out.slice(0, data.limit) };
  });

function relativeMins(mins: number): string {
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  return `${days}d ago`;
}

/* ---------------------------------------------------------------------------
 * Held-order surface — "why is this order on hold?"
 *
 * Returns active (and recently released) holds for one order, joined to the
 * tenant's hold_release_rule for the same reason when one exists. The page
 * renders this through the Policy Surface primitives so the order detail
 * explains its own hold inline rather than forcing the operator to cross-
 * reference a separate admin screen.
 *
 * This is a pure read of canonical data — no policy logic runs here.
 * ------------------------------------------------------------------------- */

const OrderHoldsZ = z.object({ orderId: z.string().uuid() });

export const getOrderHolds = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => OrderHoldsZ.parse(input))
  .handler(async ({ data, context }) => {
    const { supabase } = context;

    const { data: holds, error: hErr } = await supabase
      .from("order_holds")
      .select(
        "id, tenant_id, order_id, reason, notes, placed_at, placed_by, released_at, released_by, released_reason",
      )
      .eq("order_id", data.orderId)
      .order("placed_at", { ascending: false });
    if (hErr) throw new Error(`E_QUERY: ${hErr.message}`);

    const rows = holds ?? [];
    if (rows.length === 0) {
      return { holds: [] as Array<HoldRecord> };
    }

    // Load the governing rule per (tenant, reason) in one round trip.
    const tenantId = rows[0].tenant_id;
    // Customer-wins precedence: look up the order's customer_id once,
    // then prefer a customer rule for that reason over the tenant rule.
    const orderId = rows[0].order_id;
    const { data: ord } = await supabase
      .from("orders")
      .select("customer_id")
      .eq("id", orderId)
      .maybeSingle();
    const customerId = (ord?.customer_id as string | null | undefined) ?? null;

    const reasons = Array.from(new Set(rows.map((r) => r.reason)));
    const { data: rules } = await supabase
      .from("hold_release_rules")
      .select("id, hold_reason, trigger, params, is_active, updated_at, scope_level, customer_id")
      .eq("tenant_id", tenantId)
      .in("hold_reason", reasons);
    const ruleByReason = new Map<string, RuleSummary>();
    // Two passes: tenant rules first, then customer rules override for the
    // same reason when this order belongs to that customer.
    for (const r of (rules ?? []).filter((x: any) => x.scope_level === "tenant")) {
      ruleByReason.set(r.hold_reason as string, toRuleSummary(r));
    }
    if (customerId) {
      for (const r of (rules ?? []).filter(
        (x: any) => x.scope_level === "customer" && x.customer_id === customerId,
      )) {
        ruleByReason.set(r.hold_reason as string, toRuleSummary(r));
      }
    }

    return {
      holds: rows.map((h) => ({
        id: h.id as string,
        reason: h.reason as HoldReason,
        notes: h.notes,
        placedAt: h.placed_at as string,
        placedBy: h.placed_by,
        releasedAt: h.released_at,
        releasedBy: h.released_by,
        releasedReason: h.released_reason,
        governingRule: ruleByReason.get(h.reason as string) ?? null,
      })),
    };
  });

export type RuleSummary = {
  id: string;
  trigger: RuleTrigger;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  params: Record<string, any>;
  isActive: boolean;
  updatedAt: string;
};

export type HoldRecord = {
  id: string;
  reason: HoldReason;
  notes: string | null;
  placedAt: string;
  placedBy: string | null;
  releasedAt: string | null;
  releasedBy: string | null;
  releasedReason: string | null;
  governingRule: RuleSummary | null;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function toRuleSummary(r: any): RuleSummary {
  return {
    id: r.id as string,
    trigger: r.trigger as RuleTrigger,
    params: (r.params ?? {}) as Record<string, unknown>,
    isActive: !!r.is_active,
    updatedAt: r.updated_at as string,
  };
}